import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2, Calendar, Users, Bookmark } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { LoaderBlock, EmptyState } from '../components/listHelpers';

const TABS = [
  { id: 'businesses', label: 'Businesses', icon: Building2 },
  { id: 'events', label: 'Events', icon: Calendar },
  { id: 'communities', label: 'Communities', icon: Users },
];

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function Saved() {
  const [tab, setTab] = useState('businesses');
  const [data, setData] = useState({ businesses: [], events: [], communities: [] });
  const [loading, setLoading] = useState(true);

  async function loadAll() {
    setLoading(true);
    try {
      const [b, e, c] = await Promise.all([
        api('/businesses/saved/me').catch(() => ({ businesses: [] })),
        api('/events/saved/list').catch(() => ({ events: [] })),
        api('/communities/joined/list').catch(() => ({ communities: [] })),
      ]);
      setData({
        businesses: b.businesses || [],
        events: e.events || [],
        communities: c.communities || [],
      });
    } catch (err) {
      toast.error('Failed to load saved');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadAll(); }, []);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6" data-testid="saved-page">
      <h1 className="text-3xl font-serif font-bold text-[#2B2D42] mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>
        Saved
      </h1>
      <p className="text-sm text-[#6D6875] mb-5">All your bookmarks and joined communities in one place</p>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-white border border-[#F4F1DE] rounded-full p-1">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            data-testid={`saved-tab-${id}`}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-full text-xs sm:text-sm font-bold transition-colors ${
              tab === id ? 'bg-[#C8553D] text-white' : 'text-[#6D6875]'
            }`}
          >
            <Icon className="w-4 h-4" />
            <span className="hidden sm:inline">{label}</span> ({data[id].length})
          </button>
        ))}
      </div>

      {loading ? (
        <LoaderBlock testId="saved-loading" />
      ) : tab === 'businesses' ? (
        data.businesses.length === 0 ? (
          <EmptyState icon={Building2} title="No saved businesses yet"
            sub="Bookmark a business to save it here for later."
            action={<Link to="/app/businesses" className="mt-4 inline-flex bg-[#C8553D] hover:bg-[#9E3A26] text-white font-bold px-5 py-2.5 rounded-full text-sm">Browse Businesses</Link>}
            testid="saved-businesses-empty" />
        ) : (
          <ul className="grid sm:grid-cols-2 gap-3" data-testid="saved-businesses-list">
            {data.businesses.map((b) => (
              <li key={b.id}>
                <Link
                  to={`/app/businesses/${b.id}`}
                  data-testid={`saved-business-${b.id}`}
                  className="flex gap-3 p-3 bg-white border border-[#F4F1DE] rounded-2xl hover:bg-[#FFE5DD]/30 transition-colors"
                >
                  <div className="w-16 h-16 rounded-lg overflow-hidden bg-[#FFE5DD] flex-shrink-0">
                    {b.images?.[0] ? <img src={b.images[0]} alt={b.name} className="w-full h-full object-cover" /> : <Building2 className="w-6 h-6 text-[#C8553D]/40 m-auto mt-5" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-[#C8553D]">{b.category}</p>
                    <h3 className="font-bold text-[#2B2D42] truncate">{b.name}</h3>
                    <p className="text-xs text-[#6D6875] truncate">{b.city}</p>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )
      ) : tab === 'events' ? (
        data.events.length === 0 ? (
          <EmptyState icon={Calendar} title="No saved events yet"
            sub="Bookmark events you're considering."
            action={<Link to="/app/events" className="mt-4 inline-flex bg-[#C8553D] hover:bg-[#9E3A26] text-white font-bold px-5 py-2.5 rounded-full text-sm">Browse Events</Link>}
            testid="saved-events-empty" />
        ) : (
          <ul className="space-y-3" data-testid="saved-events-list">
            {data.events.map((e) => (
              <li key={e.id}>
                <Link
                  to={`/app/events/${e.id}`}
                  data-testid={`saved-event-${e.id}`}
                  className="flex gap-3 p-3 bg-white border border-[#F4F1DE] rounded-2xl hover:bg-[#FFE5DD]/30 transition-colors"
                >
                  <div className="w-20 h-20 rounded-lg overflow-hidden bg-[#FFE5DD] flex-shrink-0">
                    {e.image_url ? <img src={e.image_url} alt={e.title} className="w-full h-full object-cover" /> : <Calendar className="w-7 h-7 text-[#C8553D]/40 m-auto mt-6" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-[#C8553D]">{e.category}</p>
                    <h3 className="font-bold text-[#2B2D42] truncate">{e.title}</h3>
                    <p className="text-xs text-[#6D6875] truncate">{formatDate(e.date)} · {e.venue}, {e.city}</p>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )
      ) : data.communities.length === 0 ? (
        <EmptyState icon={Users} title="You haven't joined any communities yet"
          sub="Find your tribe — join groups by interest or city."
          action={<Link to="/app/communities" className="mt-4 inline-flex bg-[#C8553D] hover:bg-[#9E3A26] text-white font-bold px-5 py-2.5 rounded-full text-sm">Browse Communities</Link>}
          testid="saved-communities-empty" />
      ) : (
        <ul className="grid sm:grid-cols-2 gap-3" data-testid="saved-communities-list">
          {data.communities.map((c) => (
            <li key={c.id}>
              <Link
                to={`/app/communities/${c.id}`}
                data-testid={`saved-community-${c.id}`}
                className="flex gap-3 p-3 bg-white border border-[#F4F1DE] rounded-2xl hover:bg-[#FFE5DD]/30 transition-colors"
              >
                <div className="w-16 h-16 rounded-lg overflow-hidden bg-[#FFE5DD] flex-shrink-0">
                  {c.images?.[0] ? <img src={c.images[0]} alt={c.name} className="w-full h-full object-cover" /> : <Users className="w-6 h-6 text-[#C8553D]/40 m-auto mt-5" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-[#C8553D]">{c.category}</p>
                  <h3 className="font-bold text-[#2B2D42] truncate">{c.name}</h3>
                  <p className="text-xs text-[#6D6875] truncate">{c.city} · {(c.member_count || 0).toLocaleString()} members</p>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
